import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const h = () => ({ 'Content-Type': 'application/json' });
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
const money = (v) => v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';

const TIER_STYLES = {
  good: { bg: '#f8fafc', border: '#e2e8f0', accent: '#64748b', badge: '#e2e8f0', badgeText: '#475569' },
  better: { bg: '#eff6ff', border: '#93c5fd', accent: '#2563eb', badge: '#dbeafe', badgeText: '#1d4ed8' },
  best: { bg: '#fef9c3', border: '#fde68a', accent: '#d97706', badge: '#fde68a', badgeText: '#92400e' },
};

export default function ProposalTiers({ oppId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales/opportunities/${oppId}/expansion/proposals`, authOpts());
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, [oppId]);

  if (!expanded) {
    return (
      <div style={{ marginTop: 12 }}>
        <button data-testid="show-proposal-tiers" onClick={() => { setExpanded(true); load(); }}
          style={{ width: '100%', padding: '8px 12px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: 6,
            fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01z" />
          </svg>
          Generate Good / Better / Best Proposals
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div data-testid="proposal-tiers-loading" style={{ marginTop: 12, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: '#64748b' }}>Calculating proposals from pricing engine...</div>
      </div>
    );
  }

  if (!data || !data.tiers) {
    return (
      <div style={{ marginTop: 12, padding: 12, background: '#fefce8', borderRadius: 8, border: '1px solid #fde68a', fontSize: 12, color: '#92400e' }}>
        {data?.reason || 'Run Expansion Analysis first to generate proposal tiers.'}
        <button onClick={() => setExpanded(false)} style={{ display: 'block', marginTop: 6, fontSize: 10, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Close</button>
      </div>
    );
  }

  const tiers = data.tiers;

  return (
    <div data-testid="proposal-tiers-panel" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>Proposal Tiers</div>
        <button onClick={() => { setExpanded(false); setData(null); }} style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}>Close</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {['good', 'better', 'best'].map(key => {
          const tier = tiers[key];
          const style = TIER_STYLES[key];
          const isHighlighted = key === 'best';
          return (
            <div key={key} data-testid={`tier-${key}`}
              style={{
                padding: '12px 14px', borderRadius: 8, background: style.bg,
                border: `${isHighlighted ? 2 : 1}px solid ${style.border}`,
                position: 'relative',
              }}>
              {isHighlighted && (
                <div style={{ position: 'absolute', top: -8, right: 12, background: '#d97706', color: '#fff', fontSize: 8, fontWeight: 800, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Recommended
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: style.accent }}>{tier.label}</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>{money(tier.value)}</span>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{tier.description}</div>
              {tier.additions?.length > 0 && (
                <div style={{ marginTop: 6, borderTop: `1px solid ${style.border}`, paddingTop: 6 }}>
                  {tier.additions.map((add, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0' }}>
                      <span style={{ color: '#475569' }}>+ {add.service}</span>
                      <span style={{ color: style.accent, fontWeight: 600 }}>{money(add.potential_value)}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: style.badge, color: style.badgeText, fontWeight: 700 }}>
                      +{money(tier.lift)} ({tier.lift_percent}% lift)
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Priced Recommendations Detail */}
      {data.priced_recommendations?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>
            Pricing Engine Breakdown
          </div>
          {data.priced_recommendations.filter(r => r.priceable).map((rec, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: 11 }}>
              <div>
                <span style={{ color: '#1e293b', fontWeight: 500 }}>{rec.service}</span>
                <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 4px', borderRadius: 2, fontWeight: 700,
                  background: rec.relevance === 'HIGH' ? '#fef2f2' : '#eff6ff',
                  color: rec.relevance === 'HIGH' ? '#dc2626' : '#2563eb',
                }}>{rec.relevance}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>{money(rec.potential_value)}</div>
                {rec.customer_range?.low && (
                  <div style={{ fontSize: 9, color: '#94a3b8' }}>Range: {money(rec.customer_range.low)}–{money(rec.customer_range.high)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>
        All values from deterministic pricing engine — AI never calculates prices
      </div>
    </div>
  );
}
